import {
  ClientShortHandName,
  getProductCode,
  clientShortHandNameFromHostname,
} from '@snoam/mono-scc';
import { useSpid } from '@snoam/mono-spid';
import {
  initialize,
  getInstance,
  EventType,
  ObjectType,
  EngagementAction,
} from '@snoam/mono-pulse';
import { Enum } from 'typescript-string-enums';
import { isProduction } from './fastflowUtils';
import { IUserInfo } from './CustomTypes';
import { getTcfConfig } from '@snoam/mono-tcf';
import { tcf } from '@schibsted/sourcepoint';

export const SccPulseActivityType = Enum('Leave', 'Watch', 'Custom');
export type SccPulseActivityType = Enum<typeof SccPulseActivityType>;

export const TriggerActionType = Enum('Click', 'Swipe', 'Pinch');
export type TriggerActionType = Enum<typeof TriggerActionType>;

export const ElementActionType = Enum(
  'Show',
  'Delete',
  'Add',
  'Edit',
  'Register',
);
export type ElementActionType = Enum<typeof ElementActionType>;

export enum ElementType {
  A = 'A',
  BUTTON = 'BUTTON',
  DIV = 'DIV',
}

export const getfullUrlPath = () => {
  if (
    window.location.host.includes('.lh') ||
    window.location.host.includes('localhost')
  ) {
    return '/';
  }
  return '/s/ff/';
};

// ReferrerTag info is used to set multiple fields in the "origin" object in the pulse model
export function getReferrerTagArray(referrerString: string = null) {
  const sptCustom = sessionStorage.getItem('sptCustom');
  let referrer;
  // tslint:disable-next-line:variable-name
  const ff_url_data = localStorage.getItem('FF__ff_url_data');

  // 1.) Use Refererstring if provided
  if (referrerString) {
    referrer = referrerString;
  }
  // 2.) Use Referer from URL / FF__ff_url_data
  if (!referrer && ff_url_data) {
    referrer = JSON.parse(ff_url_data).referer;
  }
  // 3.) Use referer from cache if available
  if (!referrer && sptCustom) {
    referrer = JSON.parse(sptCustom).referrerTag;
  }

  let activeReferer;
  if (referrer) {
    if (Array.isArray(referrer)) {
      addParamToPulseSptCustom('referrerTag', referrer[0]);
      activeReferer = referrer[0];
    } else {
      addParamToPulseSptCustom('referrerTag', referrer);
      activeReferer = referrer;
    }
  }

  return referrer ? activeReferer.split('_') : [];
}

// Checks if the user is a sub at the current newspaper (i.e. not necessarily the current product). Mono-scc magic
export const updatePulseUserSubStatus: React.FunctionComponent = () => {
  const spidContext = useSpid();
  const isProd = isProduction(window.location);
  const productCode = Number(
    getProductCode(
      clientShortHandNameFromHostname(window.location.hostname),
      isProd,
    ),
  );

  if (spidContext.state.isLoaded && spidContext.state.userId) {
    spidContext.actions.hasProduct(productCode).then((hasProd) => {
      const pulse = getPulse(spidContext.state);
      pulse.setUser(
        spidContext.state.userId.toString(),
        hasProd ? 'Abo' : 'No',
      );
    });
  }

  return null;
};

// Store param values in sessionStorage to avoid losing them on redirect/refresh
export function addParamToPulseSptCustom(
  name: string,
  value: string | boolean,
) {
  const sptCustom = JSON.parse(sessionStorage.getItem('sptCustom')) || {};

  if (sptCustom[name] === value) {
    return;
  }

  sptCustom[name] = value;
  sessionStorage.setItem('sptCustom', JSON.stringify(sptCustom));
}

// Creates human readable form of name of article that linked to sales flow
export function setReferringArticleName(urlString: string = '') {
  const redirectUri = urlString || decodeURI(decodeURI(document.location.href));
  if (redirectUri.indexOf('www') < 0 || redirectUri.indexOf('referer') < 0) {
    return '';
  }

  const linkingArticleUrl = redirectUri.substring(
    redirectUri.indexOf('www'),
    redirectUri.indexOf('referer'),
  );
  if (!linkingArticleUrl || linkingArticleUrl.length === 0) {
    return;
  }
  const decodedArticleLink = decodeURI(linkingArticleUrl).slice(0, -1);
  const articleUrl = decodedArticleLink
    .split('%2F')
    .join('/')
    .replace(/['"{}]/g, '');

  sessionStorage.setItem('articleName', articleUrl);
}

const getPulse = (user: Pick<IUserInfo, 'hasProduct' | 'spidId'>) => {
  const userId = user.spidId;
  const clientShortHandName: ClientShortHandName =
    clientShortHandNameFromHostname(window.location.hostname);
  let pulse = getInstance();
  const tcfConfig = getTcfConfig(
    clientShortHandName,
    isProduction(window.location),
  );

  // @ts-expect-error - tcf is not defined
  tcf(window, document, navigator, tcfConfig);

  if (!pulse || pulse.merchantName !== clientShortHandName) {
    pulse = initialize(clientShortHandName, {
      config: {
        requireAdvertisingOptIn: true,
        autoCreateConsents: true,
      },
    });
    pulse.setUser(`${userId || ''}`, user.hasProduct ? 'Abo' : 'No');
    setReferringArticleName();
  }
  return pulse;
};

export function trackPulsePageViewEvent(
  user: Pick<IUserInfo, 'hasProduct' | 'spidId'>,
  eventName: string,
) {
  const pulse = getPulse(user);

  const referrerTagArray = getReferrerTagArray();

  const sptCustomFromStorage = JSON.parse(sessionStorage.getItem('sptCustom'));

  const articleName = sessionStorage.getItem('articleName');

  const model = pulse.createPageViewModel({
    id:
      `fastflow:` +
      eventName
        .toLowerCase()
        .replace(/\s+/g, '')
        .replace(/[^a-z]/g, ''),
    label: eventName,
    labelPrefix: 'FF',
    custom: sptCustomFromStorage,
    type: ObjectType.PAGE,
    origin: !referrerTagArray
      ? {}
      : {
          campaign: referrerTagArray[3],
          channel: referrerTagArray[1],
          content: referrerTagArray[2],
          name: articleName ? articleName : undefined,
          source: referrerTagArray[0],
        },
  });
  pulse.track(model);
  return model;
}

export function trackPulseEngagementEvent(
  user: Pick<IUserInfo, 'hasProduct' | 'spidId'>,
  eventName: string,
  moreData = {},
) {
  const pulse = getPulse(user);

  const sptCustomFromStorage = Object.assign(
    moreData,
    JSON.parse(sessionStorage.getItem('sptCustom')),
  );

  const referrerTagArray = getReferrerTagArray();

  const articleName = sessionStorage.getItem('articleName');
  const model = pulse.createEngagementModel({
    action: EngagementAction.Click,
    label: eventName,
    labelPrefix: 'FF',
    custom: sptCustomFromStorage,
    id: eventName
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[^a-z]/g, ''),
    type: ObjectType.UIELEMENT,
    elementType: '',
    pageId: 'fastflow',
    origin: {
      campaign: referrerTagArray[3],
      channel: referrerTagArray[1],
      content: referrerTagArray[2],
      id: 'fastflow',
      name: articleName,
      source: referrerTagArray[0],
      type: ObjectType.PAGE,
    },
  });
  pulse.track(model);
  return model;
}

export function getPulseProviderId(
  clientShortHandName: keyof typeof ClientShortHandName | '',
) {
  if (!clientShortHandName) {
    return '';
  }

  const clientIds: { [key in ClientShortHandName]: string } = {
    AB: '',
    BAREASANE: '',
    OMNI: '',
    OP: '',
    RB24: '',
    SVD: '',
    VEKTKLUBB: '',
    WELLOBE: '',
    AP: 'aftenposten',
    ASK: 'askoyvaeringen',
    BEDRIFT: 'schibsted', // Same as SCH
    BTI: 'bt',
    BYN: 'bygdanytt',
    DNB: 'aftenposten',
    DP: 'dinepenger',
    E24: 'dinepenger', // Same as DP
    MPL: 'aftenposten-magasin-pluss',
    SA: 'stavangeraftenblad',
    SCH: 'schibsted',
    STR: 'strilen',
    VG: 'vg',
    VNY: 'vestnytt',
  };

  return clientIds[clientShortHandName];
}

export const validHostNames: { [key in ClientShortHandName]: string } = {
  AB: 'ignored',
  BAREASANE: 'ignored',
  OMNI: 'ignored',
  OP: 'ignored',
  RB24: 'ignored',
  SVD: 'ignored',
  VEKTKLUBB: 'ignored',
  WELLOBE: 'ignored',
  AP: 'aftenposten',
  ASK: 'av-avis',
  BEDRIFT: 'Min Bedrift',
  BTI: 'bt',
  BYN: 'bygdanytt',
  DNB: 'aftenposten-dnb',
  DP: 'dinepenger',
  E24: 'e24',
  MPL: 'magasinpluss',
  SA: 'aftenbladet',
  SCH: 'schibsted',
  STR: 'strilen',
  VG: 'vg',
  VNY: 'vestnytt',
};

export function clientShortHandNameFromHostName(
  hostname: string,
): ClientShortHandName {
  return Object.keys(validHostNames).find(
    (it: keyof typeof ClientShortHandName) =>
      hostname.includes(`.${validHostNames[it] as string}`),
  ) as ClientShortHandName;
}

/*
* Removed until server tracking is working corretly.
export const pulseConfig = {
  collectorPath: 'bestilling/api/v2/ff/track',
  collectorUrl: isProduction()
    ? 'https://kundeweb.aftenposten.no'
    : 'https://kundewebtest.aftenposten.no'
};
*/

export const IntentType = Enum(
  'Accept',
  'Autobump',
  'Bump',
  'Confirm',
  'Create',
  'Delete',
  'Login',
  'Logout',
  'Open',
  'Close',
  'Hide',
  'Play',
  'Pause',
  'Purchase',
  'Share',
  'Rate',
  'Recommend',
  'Refer',
  'Reject',
  'Renew',
  'Report',
  'Save',
  'Show',
  'Unsave',
  'Send',
  'Update',
  'Dismiss',
);
export type IntentType = Enum<typeof IntentType>;

export const TargetType = Enum(
  'Application',
  'Article',
  'BusinessTransaction',
  'ClassifiedAd',
  'Content',
  'Confirmation',
  'Error',
  'Form',
  'Listing',
  'Message',
  'NotificationContent',
  'Page',
  'PhoneContact',
  'PremiumFeature',
  'Product',
  'Rating',
  'Settings',
  'Transaction',
  'UIElement',
);
export type TargetType = Enum<typeof TargetType>;

export type EventDataActivity =
  | 'trackContentView'
  | 'trackCreate'
  | 'trackMediaState'
  | 'trackExit'
  | 'trackEngagement'
  | 'trackCustom'
  | 'trackBasic';

export interface IUpdateData {
  actor: {
    id: string;
    realm: string;
  };
}

export interface IPulsePurchaseData {
  price: number;
  clientId: string;
  clientName: string;
  subScriptionId: string;
  productId: string;
  productName: string;
  onFile: string;
  paymentType: string;
  utm: IPulseEventOrigin;
}

export interface IPulseEventData {
  type: EventType;
  name: string;
  object: IPulseEventObject;
  origin?: IPulseEventOrigin;
  provider?: IPulseEventProvider;
  deployStage?: string;
  deployTag?: string;
}

export interface IPulseEventObject {
  businessTransaction?: IPulseEventObjectTransaction;
  elements?: any;
  'spt:custom': any;
  id: string;
  type: TargetType;
  name: string;
  url?: string;
}

export interface IPulseEventObjectTransaction {
  capturedAmount?: number;
  capturedVatAmount?: number;
  id: string;
  type?: TargetType;
  clientId?: string;
  clientName?: string;
  currency?: string;
  items?: object[];
  payment?: IPulseEventObjectTransactionPayment;
  subtotal?: number;
  tax?: number;
  total?: number;
}
export interface IPulseEventProvider {
  id?: string;
  type?: string;
  component?: string;
}
export interface IPulseEventObjectTransactionPayment {
  id: string;
  type: string;
  method?: string;
  onFile?: string;
  provider?: string;
}

export interface IPulseEventOrigin {
  url?: string;
  campaign?: string;
  channel?: string;
  content?: string;
  source?: string;
  term?: string;
  'spt:custom'?: object;
  referer?: string;
}

export interface IEventData {
  trackContentView?: [/*title?: */ string];
  trackCreate?: [
    /* objectType: */ string,
    /* objectId: */ string,
    /* meta?: */ object,
  ];
  trackMediaState?: [
    /* mediaId: */ string,
    /* mediaType: */ 'video' | 'audio' | 'album',
    /* activityType?: */ SccPulseActivityType,
  ];
  trackExit?: [
    /* targetId?: */ string,
    /* targetType?: */ string,
    /* activityType?: */ SccPulseActivityType,
  ];
  trackEngagement?:
    | [
        /* duration: */ number,
        /* scrollPosition: */ string,
        /* triggerAction: */ TriggerActionType,
        /* elementType: */ ElementType,
        /* elementId: */ string,
        /* elementAction: */ ElementActionType,
        /* objectMeta?: */ { [k: string]: string } | string,
      ]
    | [
        /* duration: */ number,
        /* scrollPosition: */ string,
        /* triggerAction: */ TriggerActionType,
        /* elementType: */ ElementType,
        /* elementId: */ string,
        /* elementAction: */ ElementActionType,
      ];
  trackCustom?: [
    /* activityType: */ SccPulseActivityType,
    /* customData: */ { [k: string]: string },
  ];
  trackBasic?: [
    /* eventType: */ EventType,
    /* targetType: */ TargetType,
    /* targetId: */ string,
    /* objectMeta?: */ { [k: string]: string },
    /* intent */ IntentType,
  ];
}
